/**
 * AdminAnalytics.tsx
 * 
 * Admin analytics page with detailed charts and metrics for
 * platform performance analysis and insights.
 */

import React, { useState, useEffect } from 'react'
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Bot, 
  DollarSign, 
  MessageSquare,
  Calendar,
  Activity,
  BarChart3
} from 'lucide-react'
import { adminDashboardApi } from '../services/adminApi'
import { AdminAnalytics as AdminAnalyticsType } from '../types/admin'

const AdminAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<AdminAnalyticsType | null>(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState('30d')

  useEffect(() => {
    loadAnalytics()
  }, [timeRange])

  const loadAnalytics = async () => {
    setLoading(true)
    try {
      const data = await adminDashboardApi.getAnalytics()
      setAnalytics(data)
    } catch (error) {
      console.error('Failed to load analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (!analytics) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load analytics</h3>
        <p className="text-gray-600">Please try refreshing the page.</p>
      </div>
    )
  }

  const topChatbots = analytics.chatbotUsage.slice(0, 5)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">Detailed platform performance metrics</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">
                {analytics.userGrowth[analytics.userGrowth.length - 1]?.activeUsers || 0}
              </p>
            </div>
            <div className="p-3 rounded-full bg-blue-100">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-green-600">
            <TrendingUp className="h-4 w-4 mr-1" />
            <span>+12% from last month</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Messages</p>
              <p className="text-2xl font-bold text-gray-900">
                {analytics.chatbotUsage.reduce((sum, bot) => sum + bot.messages, 0).toLocaleString()}
              </p>
            </div>
            <div className="p-3 rounded-full bg-green-100">
              <MessageSquare className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-green-600">
            <TrendingUp className="h-4 w-4 mr-1" />
            <span>+8% from last month</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                ${analytics.chatbotUsage.reduce((sum, bot) => sum + bot.revenue, 0).toLocaleString()}
              </p>
            </div>
            <div className="p-3 rounded-full bg-purple-100">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-green-600">
            <TrendingUp className="h-4 w-4 mr-1" />
            <span>+23% from last month</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Chatbots</p>
              <p className="text-2xl font-bold text-gray-900">
                {analytics.chatbotUsage.length}
              </p>
            </div>
            <div className="p-3 rounded-full bg-orange-100">
              <Bot className="h-6 w-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-green-600">
            <TrendingUp className="h-4 w-4 mr-1" />
            <span>+5% from last month</span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">User Growth</h3>
          <div className="space-y-3">
            {analytics.userGrowth.slice(-7).map((data, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{data.date}</span>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-900">{data.activeUsers} active</span>
                  <span className="text-sm text-blue-600">{data.newUsers} new</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Revenue Growth Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Growth</h3>
          <div className="space-y-3">
            {analytics.revenueGrowth.slice(-7).map((data, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{data.date}</span>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-900">${data.revenue}</span>
                  <span className="text-sm text-green-600">{data.transactions} transactions</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Performing Chatbots */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Top Performing Chatbots</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {topChatbots.map((chatbot, index) => (
              <div key={chatbot.chatbotId} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-600">{index + 1}</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{chatbot.chatbotName}</h4>
                    <p className="text-xs text-gray-500">{chatbot.users} users</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{chatbot.messages.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">messages</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-green-600">${chatbot.revenue.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">revenue</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System Health */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">System Health</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{analytics.systemHealth.cpu}%</div>
            <div className="text-sm text-gray-600">CPU Usage</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{analytics.systemHealth.memory}%</div>
            <div className="text-sm text-gray-600">Memory Usage</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{analytics.systemHealth.disk}%</div>
            <div className="text-sm text-gray-600">Disk Usage</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{analytics.systemHealth.activeConnections}</div>
            <div className="text-sm text-gray-600">Active Connections</div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminAnalytics 